import type { DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import moment from 'moment'
import React, { Fragment, ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CustomTable from 'ui/components/CustomTable/CustomTable'
import TimeField from 'ui/components/CustomTable/components/TimeField/TimeField'
import CommentField from 'ui/components/ObjectPropertyFormSelector/components/ObjectPropertySettingForm/components/PreviousSettingsTables/components/CommentField/CommentField'
import { SettingsPermissions } from 'ui/components/SettingsTable/SettingsTable'
import Datetime from 'utils/datetime/datetime'
import PriorityTag from 'views/DigitalTwinCollectedSettingsView/components/PriorityTag'
import { getPriorityLevelDisplayNames } from 'views/DigitalTwinCollectedSettingsView/DigitalTwinCollectedSettingsView.helper'
import { getLabelForAttributeValue } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import EventPostViewModal from 'views/EventsView/components/EventPostViewModal/EventPostViewModal'
import { EventPost, LinkedObjectSettingInstructions } from 'views/EventsView/components/events.helper'
import styles from './PreviousSettingsTable.module.less'
import { getCurrentActiveSettings, getHistorySettings, getUpcomingSettings } from './previousSettings.helper'
import { SettingEventLink, SettingManageButton } from './SettingActions'
import { SettingCardList, type SettingCardOrder } from './SettingCardList'

const NEWEST_UPDATE_FIRST: SettingCardOrder = { by: 'updated_at', descending: true }
const EARLIEST_START_FIRST: SettingCardOrder = { by: 'start_time', descending: false }

type DigitalTwinPreviousSettingsTableProps = {
  allSettingsForChosenProperty: DigitalTwinSetting[]
  onEditSetting: (setting: DigitalTwinSetting) => void
  permissions: SettingsPermissions
  onEventPostModalClose?: () => void
  operationalEvents?: Map<number, EventPost>
  isBaseSettings?: boolean
  priorities?: SystemSettingPriorityLevel[]
  /** One card per setting instead of table rows, for where the table's columns would be too narrow to read. */
  compact?: boolean
  eventPost?: {
    isOperationalEvent: boolean
    eventId: number | null
    linked_objects?: LinkedObjectSettingInstructions[] | null
    index?: number | null
  }
}

export default function PreviousSettingsTable({
  allSettingsForChosenProperty,
  onEditSetting,
  permissions,
  operationalEvents,
  onEventPostModalClose,
  isBaseSettings = false,
  priorities = [],
  compact = false,
  eventPost = {
    isOperationalEvent: false,
    eventId: null,
    linked_objects: null,
    index: null,
  },
}: DigitalTwinPreviousSettingsTableProps): ReactElement {
  const { t } = useTranslation()
  const [openEventPostModal, setOpenEventPostModal] = useState(false)
  const [selectedEventId, setSelectedEventId] = useState<number | null>()

  const handleEventClick = useCallback((eventId: number | null): void => {
    if (eventId === null) {
      return
    }
    setSelectedEventId(eventId)
    setOpenEventPostModal(true)
  }, [])

  const handleModalClose = useCallback(() => {
    setOpenEventPostModal(false)
    if (onEventPostModalClose !== undefined) {
      onEventPostModalClose()
    }
  }, [onEventPostModalClose])

  const generateColumns = useCallback(() => {
    const columns = [
      {
        Header: t('Start time'),
        accessor: 'startTime',
        Cell: ({ value }: { value: string | null }) => {
          // Don't write "until further notice" for start time
          if (value === null) {
            return <div>{'-'}</div>
          }

          return <TimeField value={value} />
        },
      },
      {
        Header: t('End time'),
        accessor: 'endTime',
        Cell: TimeField,
      },
      { Header: t('Value '), accessor: 'value' },
      { Header: t('Unit '), accessor: 'unit' },
      { Header: t('Cause '), accessor: 'comment', Cell: CommentField },
      {
        Header: t('Last updated'),
        accessor: 'updatedAt',
        Cell: TimeField,
        sortType: (
          rowA: { original: { updatedAt: string | number | Date } },
          rowB: { original: { updatedAt: string | number | Date } }
        ) => {
          const dateA = moment(rowA.original.updatedAt)
          const dateB = moment(rowB.original.updatedAt)
          return dateA.isAfter(dateB) ? 1 : -1
        },
      },
      { Header: t('Manage'), accessor: 'manage', disableSortBy: true },
    ]

    if (allSettingsForChosenProperty.some((setting) => setting.creator_name)) {
      columns.splice(5, 0, { Header: t('Last modified by'), accessor: 'creatorName' })
    }

    if (allSettingsForChosenProperty?.some((setting) => setting.operational_event && !eventPost.isOperationalEvent)) {
      columns.splice(6, 0, { Header: t('Event'), accessor: 'event' })
    }

    if (!isBaseSettings) {
      columns.splice(4, 0, { Header: t('Deviation type'), accessor: 'deviationType' })
      if (allSettingsForChosenProperty.some((setting) => setting.excluded_priority_layers?.length)) {
        columns.splice(5, 0, { Header: t('Excluded from'), accessor: 'excludedFrom' })
      }
    }

    return columns
  }, [allSettingsForChosenProperty, eventPost.isOperationalEvent, isBaseSettings, t])

  const activeSettingsColumns: any = useMemo(() => generateColumns(), [generateColumns])
  const historySettingsColumns: any = useMemo(() => generateColumns(), [generateColumns])
  const operationalEventSettingsColumns: any = useMemo(() => generateColumns(), [generateColumns])

  const generateRows = useCallback(
    (settings: DigitalTwinSetting[], canEdit?: boolean): Record<string, string | number | ReactElement | null>[] => {
      return settings.map((setting) => {
        const displayValue = setting.value !== null && setting.value !== undefined ? setting.value : setting.values
        const priorityLevel = priorities.find((p) => p.priority_level === setting.priority)
        return {
          id: setting.id,
          startTime: setting.start_time,
          endTime: setting.end_time === null ? null : Datetime.ISODatetimeTo59Minutes(setting.end_time),
          value: getLabelForAttributeValue(setting.attribute, displayValue) ?? '',
          unit: setting.unit ?? '-',
          comment: setting.comment,
          updatedAt: setting.updated_at,
          creatorName: setting.creator_name ?? '',
          deviationType: priorityLevel ? <PriorityTag priority={priorityLevel} size="small" /> : '-',
          excludedFrom: setting.excluded_priority_layers
            ? getPriorityLevelDisplayNames(setting.excluded_priority_layers, priorities)
            : '',
          event: setting.operational_event ? (
            <SettingEventLink
              eventName={operationalEvents?.get(setting.operational_event)?.name}
              onClick={() => handleEventClick(setting.operational_event ?? null)}
            />
          ) : (
            '-'
          ),
          manage: <SettingManageButton setting={setting} canEdit={canEdit} onEditSetting={onEditSetting} />,
        }
      })
    },
    [handleEventClick, onEditSetting, operationalEvents, priorities]
  )
  const currentActiveSettingsData = useMemo(() => {
    return generateRows(getCurrentActiveSettings(allSettingsForChosenProperty), permissions.canEdit)
  }, [allSettingsForChosenProperty, generateRows, permissions.canEdit])

  const upcomingActiveSettingsData = useMemo(() => {
    return generateRows(getUpcomingSettings(allSettingsForChosenProperty), permissions.canEdit)
  }, [allSettingsForChosenProperty, generateRows, permissions.canEdit])

  const historySettingsData = useMemo(() => {
    return generateRows(getHistorySettings(allSettingsForChosenProperty), permissions.canEditHistoric)
  }, [allSettingsForChosenProperty, generateRows, permissions.canEditHistoric])

  const operationalEventSettingsData = useMemo(() => {
    const eventSettings =
      allSettingsForChosenProperty.filter(
        (setting) =>
          eventPost.linked_objects &&
          eventPost.index !== null &&
          eventPost.index !== undefined &&
          eventPost.linked_objects[eventPost.index]?.attribute === setting.attribute &&
          setting.operational_event !== null
      ) ?? []
    return generateRows(eventSettings, permissions.canEditHistoric)
  }, [allSettingsForChosenProperty, generateRows, permissions.canEditHistoric, eventPost])

  const sortByStartTime = useMemo(() => [{ id: 'startTime', desc: false }], [])
  const sortByUpdatedAt = useMemo(() => [{ id: 'updatedAt', desc: true }], [])
  const sortByUpcomingSettings = useMemo(() => [{ id: 'startTime', desc: true }], [])

  if (eventPost.isOperationalEvent) {
    return (
      <>
        <div className={styles.DigitalTwinPreviousSettingsTable_Container}>
          <div className={styles.DigitalTwinPreviousSettingsTable_Table}>
            <CustomTable
              columns={operationalEventSettingsColumns}
              data={operationalEventSettingsData}
              initialSortBy={sortByUpcomingSettings}
            />
          </div>
        </div>
      </>
    )
  }

  const sections = [
    {
      title: t('Active settings'),
      settings: getCurrentActiveSettings(allSettingsForChosenProperty),
      canEdit: permissions.canEdit,
      columns: activeSettingsColumns,
      rows: currentActiveSettingsData,
      tableOrder: sortByUpdatedAt,
      cardOrder: NEWEST_UPDATE_FIRST,
    },
    {
      title: t('Upcoming settings'),
      settings: getUpcomingSettings(allSettingsForChosenProperty),
      canEdit: permissions.canEdit,
      columns: activeSettingsColumns,
      rows: upcomingActiveSettingsData,
      tableOrder: sortByStartTime,
      cardOrder: EARLIEST_START_FIRST,
    },
    {
      title: t('Previous settings'),
      settings: getHistorySettings(allSettingsForChosenProperty),
      canEdit: permissions.canEditHistoric,
      columns: historySettingsColumns,
      rows: historySettingsData,
      tableOrder: sortByUpdatedAt,
      cardOrder: NEWEST_UPDATE_FIRST,
    },
  ]

  return (
    <>
      <div className={styles.DigitalTwinPreviousSettingsTable_Container}>
        {sections.map((section) => (
          <Fragment key={section.title}>
            <div className={styles.DigitalTwinPreviousSettingsTable_TableTitle}>{section.title}</div>
            {compact ? (
              <SettingCardList
                settings={section.settings}
                order={section.cardOrder}
                canEdit={section.canEdit}
                priorities={priorities}
                operationalEvents={operationalEvents}
                showDeviationType={!isBaseSettings}
                onEditSetting={onEditSetting}
                onEventClick={handleEventClick}
              />
            ) : (
              <div className={styles.DigitalTwinPreviousSettingsTable_Table}>
                <CustomTable columns={section.columns} data={section.rows} initialSortBy={section.tableOrder} />
              </div>
            )}
          </Fragment>
        ))}
      </div>
      {openEventPostModal && (
        <EventPostViewModal
          closeModal={handleModalClose}
          selectedEventPostId={operationalEvents && selectedEventId !== null ? selectedEventId : undefined}
        />
      )}
    </>
  )
}
